import { ArrowLeft, Save } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button, Card, Field } from "../components/ui";
import { useAssetCustomers, useAssets } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";
import {
  currencyToCents,
  invokeAssetOperation,
  type AssetOperationAction,
} from "../lib/asset-operations";

type OperationType =
  | "counterparty"
  | "vehicle"
  | "property"
  | "vehicle-sale"
  | "property-lease"
  | "vehicle-rental"
  | "expense";

const operationMetadata: Record<
  OperationType,
  {
    title: string;
    description: string;
    action: AssetOperationAction;
    returnTo: string;
  }
> = {
  counterparty: {
    title: "Novo cliente ou empresa",
    description: "Cadastro seguro para contratos, recebíveis e segunda via.",
    action: "upsert-counterparty",
    returnTo: "/clientes",
  },
  vehicle: {
    title: "Cadastrar veículo",
    description: "Veículo para venda, estoque ou locação.",
    action: "create-vehicle",
    returnTo: "/patrimonio",
  },
  property: {
    title: "Cadastrar imóvel",
    description: "Imóvel próprio destinado à locação.",
    action: "create-property",
    returnTo: "/patrimonio",
  },
  "vehicle-sale": {
    title: "Nova venda de veículo",
    description: "Venda à vista ou parcelada com cronograma de recebíveis.",
    action: "create-vehicle-sale",
    returnTo: "/vendas-veiculos",
  },
  "property-lease": {
    title: "Nova locação de imóvel",
    description: "Contrato mensal, garantias, reajuste e cobrança.",
    action: "create-property-lease",
    returnTo: "/locacoes-imoveis",
  },
  "vehicle-rental": {
    title: "Nova locação de veículo",
    description: "Contrato para motorista de aplicativo com cobrança recorrente.",
    action: "create-vehicle-rental",
    returnTo: "/locacoes-veiculos",
  },
  expense: {
    title: "Registrar despesa de ativo",
    description: "Manutenção, imposto, seguro, multa ou reparo.",
    action: "add-expense",
    returnTo: "/despesas-ativos",
  },
};

function text(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === "string" ? value.trim() : "";
}

function money(data: FormData, key: string): string {
  return currencyToCents(text(data, key));
}

function nullableMoney(data: FormData, key: string): string {
  const value = text(data, key);
  return value ? currencyToCents(value) : "";
}

function CommonAgreementFields({
  vehiclesOnly = false,
  propertiesOnly = false,
  includeCounterparty = true,
}: {
  vehiclesOnly?: boolean;
  propertiesOnly?: boolean;
  includeCounterparty?: boolean;
}) {
  const assets = useAssets();
  const customers = useAssetCustomers();
  const filteredAssets = (assets.data ?? []).filter((asset) =>
    vehiclesOnly ? asset.type === "VEHICLE" : propertiesOnly ? asset.type === "PROPERTY" : true,
  );

  return (
    <>
      <Field label="Ativo">
        <select name="assetId" required>
          <option value="">Selecione</option>
          {filteredAssets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.code} — {asset.name}
            </option>
          ))}
        </select>
      </Field>
      {includeCounterparty && (
        <Field label="Cliente, comprador ou locatário">
          <select name="counterpartyId" required>
            <option value="">Selecione</option>
            {(customers.data ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name} — {customer.document}
              </option>
            ))}
          </select>
        </Field>
      )}
    </>
  );
}

export function AssetOperationPage() {
  const { tenant } = useTenantRuntime();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requested = params.get("tipo") ?? "counterparty";
  const type: OperationType =
    requested in operationMetadata ? (requested as OperationType) : "counterparty";
  const meta = operationMetadata[type];
  const [submitting, setSubmitting] = useState(false);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData(event.currentTarget);
      let payload: Record<string, unknown>;

      switch (type) {
        case "counterparty":
          payload = {
            externalReference: text(data, "externalReference"),
            displayName: text(data, "displayName"),
            legalName: text(data, "legalName"),
            tradeName: text(data, "tradeName"),
            identifier: text(data, "identifier"),
            billingPhone: text(data, "billingPhone"),
            billingEmail: text(data, "billingEmail"),
            status: "ACTIVE",
          };
          break;
        case "vehicle":
          payload = {
            code: text(data, "code"),
            name: text(data, "name"),
            plate: text(data, "plate"),
            renavam: text(data, "renavam"),
            chassis: text(data, "chassis"),
            make: text(data, "make"),
            model: text(data, "model"),
            trim: text(data, "trim"),
            manufactureYear: Number(text(data, "manufactureYear")),
            modelYear: Number(text(data, "modelYear")),
            color: text(data, "color"),
            fuel: text(data, "fuel"),
            usageType: text(data, "usageType"),
            mileage: Number(text(data, "mileage") || 0),
            acquisitionDate: text(data, "acquisitionDate"),
            acquisitionCostCents: nullableMoney(data, "acquisitionCost"),
            currentValueCents: nullableMoney(data, "currentValue"),
            status: "AVAILABLE",
          };
          break;
        case "property":
          payload = {
            code: text(data, "code"),
            name: text(data, "name"),
            propertyType: text(data, "propertyType"),
            street: text(data, "street"),
            number: text(data, "number"),
            complement: text(data, "complement"),
            neighborhood: text(data, "neighborhood"),
            city: text(data, "city"),
            state: text(data, "state"),
            postalCode: text(data, "postalCode"),
            registry: text(data, "registry"),
            areaSquareMeters: text(data, "areaSquareMeters"),
            bedrooms: text(data, "bedrooms"),
            bathrooms: text(data, "bathrooms"),
            parkingSpaces: text(data, "parkingSpaces"),
            furnished: data.get("furnished") === "on",
            acquisitionDate: text(data, "acquisitionDate"),
            acquisitionCostCents: nullableMoney(data, "acquisitionCost"),
            currentValueCents: nullableMoney(data, "currentValue"),
            status: "AVAILABLE",
          };
          break;
        case "vehicle-sale": {
          const salePrice = money(data, "salePrice");
          const downPayment = nullableMoney(data, "downPayment") || "0";
          const principal =
            nullableMoney(data, "financedPrincipal") ||
            (BigInt(salePrice) - BigInt(downPayment)).toString();
          payload = {
            assetId: text(data, "assetId"),
            counterpartyId: text(data, "counterpartyId"),
            saleMode: text(data, "saleMode"),
            salePriceCents: salePrice,
            downPaymentCents: downPayment,
            receivablePrincipalCents: principal,
            installmentCount: Number(text(data, "installmentCount") || 1),
            firstDueDate: text(data, "firstDueDate"),
            frequency: text(data, "frequency"),
            startsAt: today,
            lateFeeBasisPoints: Number(text(data, "lateFeeBasisPoints") || 0),
            dailyInterestBasisPoints: Number(text(data, "dailyInterestBasisPoints") || 0),
            graceDays: Number(text(data, "graceDays") || 0),
          };
          break;
        }
        case "property-lease":
          payload = {
            assetId: text(data, "assetId"),
            counterpartyId: text(data, "counterpartyId"),
            monthlyRentCents: money(data, "monthlyRent"),
            securityDepositCents: nullableMoney(data, "securityDeposit") || "0",
            guaranteeType: text(data, "guaranteeType"),
            startsAt: text(data, "startsAt"),
            endsAt: text(data, "endsAt"),
            dueDay: Number(text(data, "dueDay")),
            firstDueDate: text(data, "firstDueDate"),
            adjustmentMode: text(data, "adjustmentMode"),
            adjustmentIndex: text(data, "adjustmentIndex"),
            lateFeeBasisPoints: Number(text(data, "lateFeeBasisPoints") || 0),
            dailyInterestBasisPoints: Number(text(data, "dailyInterestBasisPoints") || 0),
          };
          break;
        case "vehicle-rental":
          payload = {
            assetId: text(data, "assetId"),
            counterpartyId: text(data, "counterpartyId"),
            driverCounterpartyId: text(data, "counterpartyId"),
            rentalAmountCents: money(data, "rentalAmount"),
            securityDepositCents: nullableMoney(data, "securityDeposit") || "0",
            frequency: text(data, "frequency"),
            startsAt: text(data, "startsAt"),
            endsAt: text(data, "endsAt"),
            dueDay: Number(text(data, "dueDay")),
            firstDueDate: text(data, "firstDueDate"),
            startMileage: Number(text(data, "startMileage") || 0),
            mileageLimitPerPeriod: text(data, "mileageLimitPerPeriod"),
            excessMileageAmountCents: nullableMoney(data, "excessMileageAmount"),
            rideHailingPlatform: text(data, "rideHailingPlatform"),
            maintenanceResponsibility: text(data, "maintenanceResponsibility"),
            insuranceResponsibility: text(data, "insuranceResponsibility"),
            trafficFineResponsibility: "DRIVER",
            lateFeeBasisPoints: Number(text(data, "lateFeeBasisPoints") || 0),
            dailyInterestBasisPoints: Number(text(data, "dailyInterestBasisPoints") || 0),
          };
          break;
        case "expense":
          payload = {
            assetId: text(data, "assetId"),
            category: text(data, "category"),
            description: text(data, "description"),
            amountCents: money(data, "amount"),
            dueDate: text(data, "dueDate"),
            status: "OPEN",
          };
          break;
      }

      await invokeAssetOperation(meta.action, tenant.slug, payload);
      toast.success(
        import.meta.env.VITE_USE_MOCKS === "true"
          ? "Operação criada no modo demonstrativo."
          : "Operação registrada com sucesso.",
      );
      navigate(meta.returnTo);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      <Link className="back-link" to={meta.returnTo}>
        <ArrowLeft size={16} /> Voltar
      </Link>
      <Card title={meta.title} description={meta.description}>
        <form className="operation-form" onSubmit={(event) => void handleSubmit(event)}>
          {type === "counterparty" && (
            <div className="form-grid">
              <Field label="Nome ou razão social">
                <input name="displayName" required />
              </Field>
              <Field label="CPF ou CNPJ">
                <input name="identifier" inputMode="numeric" required />
              </Field>
              <Field label="Razão social">
                <input name="legalName" />
              </Field>
              <Field label="Nome fantasia">
                <input name="tradeName" />
              </Field>
              <Field label="WhatsApp de cobrança">
                <input name="billingPhone" inputMode="tel" />
              </Field>
              <Field label="E-mail de cobrança">
                <input name="billingEmail" type="email" />
              </Field>
              <Field label="Código externo">
                <input name="externalReference" />
              </Field>
            </div>
          )}

          {type === "vehicle" && (
            <div className="form-grid">
              <Field label="Código interno">
                <input name="code" required />
              </Field>
              <Field label="Descrição">
                <input name="name" required />
              </Field>
              <Field label="Placa">
                <input name="plate" />
              </Field>
              <Field label="RENAVAM">
                <input name="renavam" />
              </Field>
              <Field label="Chassi">
                <input name="chassis" />
              </Field>
              <Field label="Marca">
                <input name="make" required />
              </Field>
              <Field label="Modelo">
                <input name="model" required />
              </Field>
              <Field label="Versão">
                <input name="trim" />
              </Field>
              <Field label="Ano fabricação">
                <input name="manufactureYear" type="number" required />
              </Field>
              <Field label="Ano modelo">
                <input name="modelYear" type="number" required />
              </Field>
              <Field label="Cor">
                <input name="color" />
              </Field>
              <Field label="Combustível">
                <input name="fuel" />
              </Field>
              <Field label="Uso">
                <select name="usageType">
                  <option value="STOCK">Estoque</option>
                  <option value="RIDE_HAILING">Motorista de aplicativo</option>
                  <option value="PRIVATE">Uso próprio</option>
                  <option value="OTHER">Outro</option>
                </select>
              </Field>
              <Field label="Quilometragem">
                <input name="mileage" type="number" min="0" defaultValue="0" />
              </Field>
              <Field label="Data da aquisição">
                <input name="acquisitionDate" type="date" />
              </Field>
              <Field label="Custo de aquisição">
                <input name="acquisitionCost" placeholder="0,00" />
              </Field>
              <Field label="Valor atual">
                <input name="currentValue" placeholder="0,00" />
              </Field>
            </div>
          )}

          {type === "property" && (
            <div className="form-grid">
              <Field label="Código interno">
                <input name="code" required />
              </Field>
              <Field label="Descrição">
                <input name="name" required />
              </Field>
              <Field label="Tipo">
                <select name="propertyType">
                  <option value="HOUSE">Casa</option>
                  <option value="APARTMENT">Apartamento</option>
                  <option value="COMMERCIAL">Comercial</option>
                  <option value="LAND">Terreno</option>
                  <option value="WAREHOUSE">Barracão</option>
                  <option value="OTHER">Outro</option>
                </select>
              </Field>
              <Field label="Matrícula">
                <input name="registry" />
              </Field>
              <Field label="Rua">
                <input name="street" required />
              </Field>
              <Field label="Número">
                <input name="number" />
              </Field>
              <Field label="Complemento">
                <input name="complement" />
              </Field>
              <Field label="Bairro">
                <input name="neighborhood" />
              </Field>
              <Field label="Cidade">
                <input name="city" required />
              </Field>
              <Field label="Estado">
                <input name="state" maxLength={2} required />
              </Field>
              <Field label="CEP">
                <input name="postalCode" />
              </Field>
              <Field label="Área m²">
                <input name="areaSquareMeters" />
              </Field>
              <Field label="Quartos">
                <input name="bedrooms" type="number" min="0" />
              </Field>
              <Field label="Banheiros">
                <input name="bathrooms" type="number" min="0" />
              </Field>
              <Field label="Vagas">
                <input name="parkingSpaces" type="number" min="0" />
              </Field>
              <Field label="Mobiliado">
                <input name="furnished" type="checkbox" />
              </Field>
              <Field label="Data da aquisição">
                <input name="acquisitionDate" type="date" />
              </Field>
              <Field label="Custo de aquisição">
                <input name="acquisitionCost" placeholder="0,00" />
              </Field>
              <Field label="Valor atual">
                <input name="currentValue" placeholder="0,00" />
              </Field>
            </div>
          )}

          {type === "vehicle-sale" && (
            <div className="form-grid">
              <CommonAgreementFields vehiclesOnly />
              <Field label="Forma de venda">
                <select name="saleMode">
                  <option value="INSTALLMENT">Parcelada</option>
                  <option value="CASH">À vista</option>
                </select>
              </Field>
              <Field label="Preço da venda">
                <input name="salePrice" placeholder="58.000,00" required />
              </Field>
              <Field label="Entrada">
                <input name="downPayment" placeholder="10.000,00" />
              </Field>
              <Field label="Principal parcelado">
                <input name="financedPrincipal" placeholder="48.000,00" />
              </Field>
              <Field label="Quantidade de parcelas">
                <input name="installmentCount" type="number" min="1" defaultValue="24" required />
              </Field>
              <Field label="Primeiro vencimento">
                <input name="firstDueDate" type="date" required />
              </Field>
              <Field label="Frequência">
                <select name="frequency">
                  <option value="MONTHLY">Mensal</option>
                  <option value="WEEKLY">Semanal</option>
                  <option value="ONCE">Única</option>
                </select>
              </Field>
              <Field label="Multa (%)">
                <input name="lateFeeBasisPoints" type="number" min="0" defaultValue="200" />
              </Field>
              <Field label="Juros diário (basis points)">
                <input name="dailyInterestBasisPoints" type="number" min="0" defaultValue="33" />
              </Field>
              <Field label="Carência de atraso (dias)">
                <input name="graceDays" type="number" min="0" defaultValue="0" />
              </Field>
            </div>
          )}

          {type === "property-lease" && (
            <div className="form-grid">
              <CommonAgreementFields propertiesOnly />
              <Field label="Aluguel mensal">
                <input name="monthlyRent" placeholder="1.850,00" required />
              </Field>
              <Field label="Caução">
                <input name="securityDeposit" placeholder="1.850,00" />
              </Field>
              <Field label="Garantia">
                <select name="guaranteeType">
                  <option value="NONE">Sem garantia</option>
                  <option value="DEPOSIT">Caução</option>
                  <option value="GUARANTOR">Fiador</option>
                  <option value="INSURANCE">Seguro-fiança</option>
                </select>
              </Field>
              <Field label="Início">
                <input name="startsAt" type="date" required />
              </Field>
              <Field label="Fim">
                <input name="endsAt" type="date" />
              </Field>
              <Field label="Dia de vencimento">
                <input name="dueDay" type="number" min="1" max="28" defaultValue="10" required />
              </Field>
              <Field label="Primeiro vencimento">
                <input name="firstDueDate" type="date" required />
              </Field>
              <Field label="Reajuste">
                <select name="adjustmentMode">
                  <option value="INDEX">Índice</option>
                  <option value="FIXED">Fixo</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </Field>
              <Field label="Índice">
                <input name="adjustmentIndex" defaultValue="IPCA" />
              </Field>
              <Field label="Multa (%)">
                <input name="lateFeeBasisPoints" type="number" min="0" defaultValue="200" />
              </Field>
              <Field label="Juros diário (basis points)">
                <input name="dailyInterestBasisPoints" type="number" min="0" defaultValue="33" />
              </Field>
            </div>
          )}

          {type === "vehicle-rental" && (
            <div className="form-grid">
              <CommonAgreementFields vehiclesOnly />
              <Field label="Valor da locação">
                <input name="rentalAmount" placeholder="650,00" required />
              </Field>
              <Field label="Caução">
                <input name="securityDeposit" placeholder="1.300,00" />
              </Field>
              <Field label="Frequência">
                <select name="frequency">
                  <option value="WEEKLY">Semanal</option>
                  <option value="MONTHLY">Mensal</option>
                  <option value="DAILY">Diária</option>
                </select>
              </Field>
              <Field label="Início">
                <input name="startsAt" type="date" required />
              </Field>
              <Field label="Fim">
                <input name="endsAt" type="date" />
              </Field>
              <Field label="Dia de vencimento">
                <input name="dueDay" type="number" min="1" max="28" defaultValue="5" required />
              </Field>
              <Field label="Primeiro vencimento">
                <input name="firstDueDate" type="date" required />
              </Field>
              <Field label="Quilometragem inicial">
                <input name="startMileage" type="number" min="0" defaultValue="0" />
              </Field>
              <Field label="Limite por período">
                <input name="mileageLimitPerPeriod" type="number" min="0" />
              </Field>
              <Field label="Excedente por km">
                <input name="excessMileageAmount" placeholder="1,00" />
              </Field>
              <Field label="Plataforma">
                <input name="rideHailingPlatform" defaultValue="UBER" />
              </Field>
              <Field label="Manutenção">
                <select name="maintenanceResponsibility">
                  <option value="OWNER">Proprietário</option>
                  <option value="DRIVER">Motorista</option>
                  <option value="SHARED">Compartilhada</option>
                </select>
              </Field>
              <Field label="Seguro">
                <select name="insuranceResponsibility">
                  <option value="OWNER">Proprietário</option>
                  <option value="DRIVER">Motorista</option>
                  <option value="SHARED">Compartilhado</option>
                </select>
              </Field>
              <Field label="Multa (%)">
                <input name="lateFeeBasisPoints" type="number" min="0" defaultValue="200" />
              </Field>
              <Field label="Juros diário (basis points)">
                <input name="dailyInterestBasisPoints" type="number" min="0" defaultValue="33" />
              </Field>
            </div>
          )}

          {type === "expense" && (
            <div className="form-grid">
              <CommonAgreementFields includeCounterparty={false} />
              <Field label="Categoria">
                <select name="category">
                  <option value="MAINTENANCE">Manutenção</option>
                  <option value="INSURANCE">Seguro</option>
                  <option value="TAX">Imposto</option>
                  <option value="LICENSING">Licenciamento</option>
                  <option value="CONDOMINIUM">Condomínio</option>
                  <option value="PROPERTY_TAX">IPTU</option>
                  <option value="REPAIR">Reparo</option>
                  <option value="FINE">Multa</option>
                  <option value="OTHER">Outro</option>
                </select>
              </Field>
              <Field label="Descrição">
                <input name="description" required />
              </Field>
              <Field label="Valor">
                <input name="amount" placeholder="0,00" required />
              </Field>
              <Field label="Vencimento">
                <input name="dueDate" type="date" />
              </Field>
            </div>
          )}

          <div className="card-actions">
            <Button variant="secondary" type="button" onClick={() => navigate(meta.returnTo)}>
              Cancelar
            </Button>
            <Button disabled={submitting} type="submit">
              <Save size={16} />
              {submitting ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
